import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ElementRef,
  AfterViewChecked,
} from '@angular/core';
import { interval, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { WebLiveChatService } from 'src/app/private/services/web-live-chat.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import {
  parseWebLiveChatWebSocketFrame,
  websocketMessageDataAsString,
  buildWebSocketUrl,
  resolveWebChatWsBase,
} from 'src/app/private/utils/web-live-chat-ws.util';
import { liveChatUserMessage, LIVE_CHAT_SESSION_ENDED_MESSAGE } from 'src/app/private/utils/live-chat-user-message';
import { sortWebChatMessages } from 'src/app/private/utils/web-live-chat-messages.util';
import { playIncomingChatRing } from 'src/app/private/utils/live-chat-notification.util';
import { environment } from 'src/environments/environment';

type QueueFilter = 'all' | 'open' | 'unassigned' | 'resolved';
type RightPanelTab = 'visitor' | 'crm' | 'previous';
type ComposerTab = 'reply' | 'note';
type SortOrder = 'newest' | 'oldest';

@Component({
  selector: 'app-web-live-chat',
  templateUrl: './web-live-chat.component.html',
  styleUrls: ['./web-live-chat.component.scss'],
})
export class WebLiveChatComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('msgThread') msgThread?: ElementRef<HTMLDivElement>;

  private destroy$ = new Subject<void>();
  private ws: WebSocket | null = null;
  private lastMessageUlid = '';
  private shouldScroll = false;
  private knownWaitingIds = new Set<string>();
  private queueAlertsReady = false;
  private ringTimer: ReturnType<typeof setInterval> | null = null;
  private ringSuppressed = false;

  clientId = '';
  agentId = '';
  agentName = '';
  loading = false;
  messagesLoading = false;
  sending = false;
  takingOver = false;
  resolving = false;

  waiting: any[] = [];
  active: any[] = [];
  filteredSessions: any[] = [];
  selected: any = null;
  messages: any[] = [];
  draft = '';

  searchQuery = '';
  activeFilter: QueueFilter = 'all';
  sortOrder: SortOrder = 'newest';
  rightPanelTab: RightPanelTab = 'visitor';
  composerTab: ComposerTab = 'reply';
  showEmbedSetup = false;
  visitorNote = '';
  showPendingBanner = false;
  selectedClosed = false;
  selectedClosedBy: 'visitor' | 'agent' | null = null;

  embedTokenMissing = false;
  embedScriptLine = '';
  private chatEmbedRawUrl = '';

  readonly FILTERS: { key: QueueFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'open', label: 'Open' },
    { key: 'unassigned', label: 'Unassigned' },
    { key: 'resolved', label: 'Resolved' },
  ];

  constructor(
    private webChat: WebLiveChatService,
    private localStorage: LocalStorageService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.clientId = this.localStorage.getClientId() || '';
    this.agentId = this.localStorage.getUserId() || '';
    this.agentName = this.localStorage.getProfileName() || '';
    this.initEmbedScript();
    this.loadQueue();
    interval(5000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loadQueue(true);
        if (this.selected) this.loadMessages(true);
      });
  }

  ngAfterViewChecked(): void {
    if (this.shouldScroll) {
      this.scrollToBottom();
      this.shouldScroll = false;
    }
  }

  ngOnDestroy(): void {
    this.stopWaitingRing();
    this.closeWs();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get stats() {
    return {
      waiting: this.waiting.length,
      active: this.active.length,
      total: this.waiting.length + this.active.length,
      resolved: 0,
    };
  }

  get recentActivityMessages(): any[] {
    return this.messages
      .filter((m) => m.sender_type === 'agent' || m.sender_type === 'customer')
      .slice(-3)
      .reverse();
  }

  filterCount(key: QueueFilter): number {
    if (key === 'all') return this.stats.total;
    if (key === 'open') return this.stats.active;
    if (key === 'unassigned') return this.stats.waiting;
    return this.stats.resolved;
  }

  loadQueue(silent = false): void {
    if (!this.clientId) return;
    if (!silent) this.loading = true;
    this.webChat.getWebChatQueue({ client_id: this.clientId, limit: 50 }).subscribe({
      next: (res) => {
        const prevWaiting = this.waiting;
        this.waiting = res.waiting || [];
        this.active = res.active || [];
        this.handleWaitingAlerts(prevWaiting, this.waiting);
        this.showPendingBanner = this.waiting.length > 0;
        this.applyFilters();
        if (this.selected) {
          const updated = [...this.waiting, ...this.active].find(
            (s) => s.session_id === this.selected.session_id
          );
          if (updated) {
            this.selected = updated;
            this.selectedClosed = false;
            this.selectedClosedBy = null;
          } else if (!this.selectedClosed && (this.isActive(this.selected) || this.isWaiting(this.selected))) {
            this.markSessionEnded('visitor');
          }
        }
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  setFilter(key: QueueFilter): void {
    this.activeFilter = key;
    this.applyFilters();
  }

  applyFilters(): void {
    let list: any[] = [];
    if (this.activeFilter === 'open') list = [...this.active];
    else if (this.activeFilter === 'unassigned') list = [...this.waiting];
    else if (this.activeFilter === 'resolved') list = [];
    else list = [...this.waiting, ...this.active];

    const q = this.searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (s) =>
          String(s.customer_name || '').toLowerCase().includes(q) ||
          String(s.subject || '').toLowerCase().includes(q) ||
          String(s.customer_email || '').toLowerCase().includes(q)
      );
    }

    list.sort((a, b) => {
      const ta = new Date(a.last_message_at || a.created_at || 0).getTime();
      const tb = new Date(b.last_message_at || b.created_at || 0).getTime();
      return this.sortOrder === 'newest' ? tb - ta : ta - tb;
    });

    this.filteredSessions = list;
  }

  selectSession(session: any): void {
    this.selected = session;
    this.selectedClosed = false;
    this.selectedClosedBy = null;
    this.rightPanelTab = 'visitor';
    this.composerTab = 'reply';
    this.messages = [];
    this.lastMessageUlid = '';
    this.loadMessages();
  }

  sessionPreview(session: any): string {
    if (session?.subject) return session.subject;
    if (this.selected?.session_id === session?.session_id && this.messages.length) {
      const last = this.messages[this.messages.length - 1];
      if (last?.content) return String(last.content).slice(0, 80);
    }
    return 'New chat request';
  }

  statusLabel(session: any): string {
    if (this.selected?.session_id === session?.session_id && this.selectedClosed) return 'Resolved';
    const status = String(session?.status || '').toUpperCase();
    if (status === 'WAITING') return 'Pending';
    if (status === 'ACTIVE') return 'Open';
    if (status === 'CLOSED') return 'Resolved';
    return status || 'Open';
  }

  statusPillClass(session: any): string {
    const label = this.statusLabel(session).toLowerCase();
    if (label === 'pending') return 'pill-pending';
    if (label === 'open') return 'pill-open';
    if (label === 'resolved') return 'pill-resolved';
    return 'pill-open';
  }

  chatDuration(): string {
    if (!this.selected?.created_at) return '—';
    const start = new Date(this.selected.created_at).getTime();
    if (Number.isNaN(start)) return '—';
    const end = this.selectedClosed ? Date.now() : Date.now();
    const sec = Math.max(0, Math.floor((end - start) / 1000));
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
  }

  formatDateTime(iso: string | undefined): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  saveVisitorNote(): void {
    if (!this.visitorNote.trim()) return;
    this.toastr.success('Note saved locally');
    this.composerTab = 'reply';
  }

  jumpToSession(session: any): void {
    this.selectSession(session);
    if (this.isWaiting(session)) this.takeover();
  }

  dismissPendingBanner(): void {
    this.showPendingBanner = false;
    this.ringSuppressed = true;
    this.stopWaitingRing();
  }

  async copyEmbedScript(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.embedScriptLine);
      this.toastr.success('Embed script copied');
    } catch {
      this.toastr.error('Could not copy to clipboard');
    }
  }

  openChatPreview(): void {
    if (this.embedTokenMissing || !this.chatEmbedRawUrl) return;
    window.open(this.chatEmbedRawUrl, '_blank', 'noopener,noreferrer');
  }

  loadMessages(silent = false): void {
    if (!this.selected?.session_id) return;
    if (!silent) this.messagesLoading = true;
    this.webChat
      .getWebChatMessages({
        client_id: this.clientId,
        session_id: this.selected.session_id,
        limit: 100,
      })
      .subscribe({
        next: (res) => {
          this.messages = sortWebChatMessages(res.items || []);
          const last = this.messages[this.messages.length - 1];
          if (last?.message_id) this.lastMessageUlid = last.message_id;
          this.messagesLoading = false;
          this.shouldScroll = true;
        },
        error: () => {
          this.messagesLoading = false;
          if (!silent) this.toastr.error('Could not load messages');
        },
      });
  }

  takeover(): void {
    if (!this.selected || this.takingOver) return;
    this.takingOver = true;
    this.webChat
      .takeoverWebChat({
        session_id: this.selected.session_id,
        client_id: this.clientId,
        agent_id: this.agentId,
        agent_name: this.agentName,
      })
      .subscribe({
        next: (res) => {
          this.takingOver = false;
          this.selected = {
            ...this.selected,
            status: 'ACTIVE',
            agent_name: this.agentName,
          };
          this.stopWaitingRing();
          const wsUrl = buildWebSocketUrl(res?.ws_url || resolveWebChatWsBase(), res?.ws_token || '');
          if (wsUrl) this.connectWs(wsUrl);
          this.loadQueue(true);
          this.loadMessages(true);
          this.toastr.success('Chat accepted');
        },
        error: (err) => {
          this.takingOver = false;
          this.toastr.error(liveChatUserMessage(err, 'Could not take this chat'));
        },
      });
  }

  send(): void {
    const text = this.draft.trim();
    if (!text || !this.selected || this.sending || this.isWaiting(this.selected) || this.selectedClosed) return;
    this.sending = true;
    this.webChat
      .sendWebChatMessage({
        session_id: this.selected.session_id,
        client_id: this.clientId,
        agent_id: this.agentId,
        agent_name: this.agentName,
        content: text,
      })
      .subscribe({
        next: () => {
          this.draft = '';
          this.sending = false;
          this.loadMessages(true);
        },
        error: (err) => {
          this.sending = false;
          if (this.isSessionClosedError(err)) {
            this.markSessionEnded('visitor');
            return;
          }
          this.toastr.error(liveChatUserMessage(err, 'Message could not be sent'));
        },
      });
  }

  onEnter(e: KeyboardEvent): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.send();
    }
  }

  resolve(): void {
    if (!this.selected || this.resolving) return;
    if (this.selectedClosed) {
      this.dismissEndedSession();
      return;
    }
    this.resolving = true;
    this.webChat
      .resolveWebChat({
        session_id: this.selected.session_id,
        client_id: this.clientId,
        agent_id: this.agentId,
        agent_name: this.agentName,
      })
      .subscribe({
        next: () => {
          this.resolving = false;
          this.dismissEndedSession();
          this.loadQueue(true);
          this.toastr.success('Chat resolved');
        },
        error: (err) => {
          this.resolving = false;
          if (this.isSessionClosedError(err)) {
            this.markSessionEnded('visitor', false);
            this.toastr.info(LIVE_CHAT_SESSION_ENDED_MESSAGE, 'Chat ended');
            return;
          }
          this.toastr.error(liveChatUserMessage(err, 'Could not resolve chat'));
        },
      });
  }

  dismissEndedSession(): void {
    this.closeWs();
    this.selected = null;
    this.messages = [];
    this.draft = '';
    this.selectedClosed = false;
    this.selectedClosedBy = null;
  }

  isClosed(session: any = this.selected): boolean {
    if (this.selectedClosed) return true;
    return String(session?.status || '').toUpperCase() === 'CLOSED';
  }

  isWaiting(session: any): boolean {
    return String(session?.status || '').toUpperCase() === 'WAITING';
  }

  isActive(session: any): boolean {
    return String(session?.status || '').toUpperCase() === 'ACTIVE';
  }

  formatTime(iso: string | undefined): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  formatRelative(iso: string | undefined): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    return d.toLocaleDateString();
  }

  bubbleClass(senderType: string): string {
    const t = String(senderType || '').toLowerCase();
    if (t === 'agent') return 'wlc-bubble agent';
    if (t === 'system') return 'wlc-bubble system';
    return 'wlc-bubble customer';
  }

  getInitials(label: string | undefined): string {
    const parts = String(label || '?')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    }
    const s = parts[0] || '?';
    return s.slice(0, 2).toUpperCase();
  }

  avatarColor(seed: string | undefined): string {
    const palette = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444'];
    const s = String(seed || 'visitor');
    let hash = 0;
    for (let i = 0; i < s.length; i++) hash = s.charCodeAt(i) + ((hash << 5) - hash);
    return palette[Math.abs(hash) % palette.length];
  }

  private handleWaitingAlerts(previous: any[], current: any[]): void {
    const currentIds = new Set(
      current.map((s) => String(s?.session_id || '')).filter(Boolean)
    );

    const hasNewWaiting = this.queueAlertsReady
      && [...currentIds].some((id) => !this.knownWaitingIds.has(id));

    if (hasNewWaiting) {
      this.ringSuppressed = false;
      playIncomingChatRing();
      this.toastr.info(
        current.length === 1 ? 'New visitor waiting for chat' : `${current.length} visitors waiting`,
        'Incoming chat',
        { timeOut: 5000 }
      );
    }

    this.knownWaitingIds = currentIds;
    this.queueAlertsReady = true;
    this.syncWaitingRing(current.length);
  }

  private syncWaitingRing(waitingCount: number): void {
    if (waitingCount === 0) {
      this.ringSuppressed = false;
      this.stopWaitingRing();
      return;
    }
    if (!this.ringSuppressed && !this.ringTimer) {
      this.ringTimer = setInterval(() => playIncomingChatRing(), 5000);
    }
  }

  private stopWaitingRing(): void {
    if (this.ringTimer) {
      clearInterval(this.ringTimer);
      this.ringTimer = null;
    }
  }

  private isSessionClosedError(err: unknown): boolean {
    const e = err as { error?: { code?: string; message?: string }; message?: string };
    const code = e?.error?.code || (e?.error as any)?.data?.code;
    if (code === 'SESSION_CLOSED') return true;
    const msg = liveChatUserMessage(err, '');
    return msg === LIVE_CHAT_SESSION_ENDED_MESSAGE || /already closed|session is closed/i.test(msg);
  }

  private markSessionEnded(by: 'visitor' | 'agent', notify = true): void {
    if (!this.selected || this.selectedClosed) return;
    this.selectedClosed = true;
    this.selectedClosedBy = by;
    this.selected = { ...this.selected, status: 'CLOSED' };
    this.closeWs();
    this.draft = '';
    const systemLine =
      by === 'visitor' ? 'The visitor ended the chat.' : 'This chat has been resolved.';
    const hasLine = this.messages.some((m) => m.sender_type === 'system' && String(m.content || '').includes('ended'));
    if (!hasLine) {
      this.messages = sortWebChatMessages([
        ...this.messages,
        {
          sender_type: 'system',
          content: systemLine,
          created_at: new Date().toISOString(),
        },
      ]);
      this.shouldScroll = true;
    }
    if (notify && by === 'visitor') {
      this.toastr.info('The visitor ended this chat.', 'Chat ended');
    }
  }

  private scrollToBottom(): void {
    const el = this.msgThread?.nativeElement;
    if (el) el.scrollTop = el.scrollHeight;
  }

  private connectWs(url: string): void {
    this.closeWs();
    if (!url) return;
    const socket = new WebSocket(url);
    this.ws = socket;
    socket.addEventListener('message', (ev) => {
      void (async () => {
        try {
          const raw = await websocketMessageDataAsString(ev);
          const parsed = parseWebLiveChatWebSocketFrame(raw);
          if (parsed?.messageId) this.lastMessageUlid = parsed.messageId;
          if (parsed?.sessionEnded) {
            this.markSessionEnded('visitor');
            this.loadQueue(true);
            return;
          }
          if (parsed && parsed.role !== 'AGENT') {
            this.loadMessages(true);
          }
        } catch {
          /* ignore */
        }
      })();
    });
  }

  private closeWs(): void {
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        /* ignore */
      }
      this.ws = null;
    }
  }

  private initEmbedScript(): void {
    const embedToken = (localStorage.getItem('embed_token') || '').trim();
    if (!embedToken) {
      this.embedTokenMissing = true;
      return;
    }
    this.embedTokenMissing = false;
    this.chatEmbedRawUrl = this.buildChatEmbedUrl(embedToken);
    this.embedScriptLine = this.buildEmbedScriptLine(embedToken);
  }

  private buildChatEmbedUrl(embedToken: string): string {
    const qs = new URLSearchParams();
    qs.set('embed_token', embedToken);
    const userId = String(this.agentId || '').trim();
    if (userId) qs.set('user_id', userId);
    const clientId = String(this.clientId || '').trim();
    if (clientId) qs.set('client_id', clientId);
    return `${window.location.origin}/#/chat-embed?${qs.toString()}`;
  }

  private buildEmbedScriptLine(embedToken: string): string {
    const base = this.chatWidgetScriptBase();
    const qs = new URLSearchParams();
    qs.set('embed_token', embedToken);
    return `<script defer src="${base}/chat-widget.js?${qs.toString()}"><\/script>`;
  }

  private chatWidgetScriptBase(): string {
    const configured = String(environment.chatWidgetScriptBase || '').trim();
    return configured.replace(/\/$/, '') || window.location.origin;
  }
}
